import React, { Component } from "react";
import { Field } from "formik";
import PropTypes, { string, arrayOf, shape, func, oneOf } from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "redux";

import * as administrationUtils from "util/administration";
import { permissionsSelectors } from "reducers/administration";

import { PermissionsPanelContext } from "pages/administration/_components/advancedPermissionsForm/PermissionsPanel";
import IndeterminateCheckbox from "pages/_components/fields/IndeterminateCheckbox";
import I18n from "pages/_components/I18n";
import withContext from "pages/_components/withContext";
import { AccordionContext } from "pages/_components/Accordion";

class PermissionField extends Component {
    static propTypes = {
        idItem: string.isRequired,
        label: string.isRequired,
        permissionList: arrayOf(
            shape({
                productTypes: string,
            }),
        ).isRequired,
        products: arrayOf(
            shape({
                productType: string,
            }),
        ),
        accordionContext: shape({
            itemOpen: PropTypes.number,
            onClick: func.isRequired,
        }).isRequired,
        permissionsPanelContext: shape({
            activeIdItem: string,
            onBlur: func.isRequired,
            onClick: func.isRequired,
        }).isRequired,
        number: PropTypes.number,
        parentOption: shape({
            number: PropTypes.number,
        }),
        mode: oneOf(["view", "edit"]).isRequired,
        level: PropTypes.number,
        permissionsToShow: shape({}),
    };

    static defaultProps = {
        products: [],
        number: null,
        parentOption: null,
        level: null,
        permissionsToShow: null,
    };

    componentDidUpdate({ accordionContext }) {
        const { itemOpen: prevItemOpen } = accordionContext;
        const {
            permissionsPanelContext: { onBlur, activeIdItem },
            accordionContext: { itemOpen },
            number,
            idItem,
            parentOption,
        } = this.props;
        const index = number !== null ? number : parentOption.number;

        // If another accordion item was opened, close this panel
        if (prevItemOpen === index && itemOpen !== index && activeIdItem === idItem) {
            onBlur();
        }
    }

    handleCheckClick = ({ field, form }, productTypes) => {
        const { setValues, values } = form;
        const { name } = field;

        setValues({
            ...values,
            permissions: {
                ...values.permissions,
                [name]: values.permissions[name] && values.permissions[name].length ? [] : productTypes,
            },
        });
    };

    handleClick = () => {
        const {
            permissionsPanelContext: { onClick: panelHandleClick },
            accordionContext: { onClick: accordionHandleClick, itemOpen },
            number,
            parentOption,
        } = this.props;

        const index = number !== null ? number : parentOption.number;

        if (itemOpen !== index) {
            accordionHandleClick(index);
        }
        panelHandleClick(this.props);
    };

    render() {
        const {
            permissionsPanelContext: { activeIdItem },
            idItem,
            label,
            permissionList,
            products,
            mode,
            level,
            permissionsToShow,
        } = this.props;
        const [permissions] = permissionList;

        return (
            <Field name={idItem}>
                {(props) => {
                    const { form } = props;
                    const allValues = form.values.permissions ? form.values.permissions[idItem] || [] : [];
                    let values = [...allValues];

                    const productTypes = permissions.productTypes ? permissions.productTypes.split(",") : [];
                    const mappedProductTypes = productTypes.map((productType) => `ALL_${productType}`);
                    const filteredProducts = products.filter(({ productType }) => productTypes.includes(productType));

                    const { selectedSmartGroupAmount, smartGroupAmount } = administrationUtils.smartGroupsOptionsCount(
                        productTypes,
                        values,
                    );
                    const { selectedProductsAmount, productsAmount } = administrationUtils.productsOptionsCount(
                        filteredProducts.map(({ value }) => ({ idProduct: value })),
                        values,
                    );
                    const smartGroupAmountText = `${selectedSmartGroupAmount}/${smartGroupAmount}`;
                    const productsAmountText = `${selectedProductsAmount}/${productsAmount}`;

                    if (mode !== "edit") {
                        values = permissionsToShow ? permissionsToShow[idItem] : values;
                    }

                    if (level) {
                        return (
                            <button
                                className={classNames("collapsable-button px-0", {
                                    "is-active": activeIdItem === idItem,
                                })}
                                type="button"
                                onClick={this.handleClick}>
                                {level === 1 ? (
                                    <span>
                                        <ul>
                                            <li>
                                                <span className="list-item-hint">
                                                    {smartGroupAmountText}{" "}
                                                    <I18n id="administration.permissions.advanced.smart.groups" />
                                                </span>
                                            </li>
                                            <li>
                                                <span className="list-item-hint">
                                                    {productsAmountText}{" "}
                                                    <I18n id="administration.permissions.advanced.products" />
                                                </span>
                                            </li>
                                        </ul>
                                    </span>
                                ) : (
                                    <span>
                                        <span>{label}</span>{" "}
                                        <span className="list-item-hint">
                                            {smartGroupAmountText}{" "}
                                            <I18n id="administration.permissions.advanced.smart.groups" />
                                        </span>
                                        {" - "}
                                        <span className="list-item-hint">
                                            {productsAmountText}{" "}
                                            <I18n id="administration.permissions.advanced.products" />
                                        </span>
                                    </span>
                                )}
                            </button>
                        );
                    }

                    const hasProducts = filteredProducts.some((p) => values.includes(p.value));
                    const hasProductTypes = productTypes.some((p) => values.includes(`ALL_${p}`));

                    return mode === "edit" ? (
                        <button
                            className={classNames("collapsable-button px-0", {
                                "is-active": activeIdItem === idItem,
                            })}
                            type="button"
                            onClick={this.handleClick}>
                            <>
                                <IndeterminateCheckbox
                                    id={idItem}
                                    onCheckClick={() => this.handleCheckClick(props, mappedProductTypes)}
                                    className="c-control c-control--has-icon c-control--checkbox"
                                    selectedOptionsAmount={
                                        selectedSmartGroupAmount > selectedProductsAmount
                                            ? selectedSmartGroupAmount
                                            : selectedProductsAmount
                                    }
                                    optionsAmount={
                                        selectedSmartGroupAmount > selectedProductsAmount
                                            ? smartGroupAmount
                                            : productsAmount
                                    }
                                />
                                <span>
                                    <span className="group-permission-item-header">{label}</span>{" "}
                                    <span className="list-item-hint">
                                        {smartGroupAmountText}{" "}
                                        <I18n id="administration.permissions.advanced.smart.groups" />
                                    </span>
                                    {" - "}
                                    <span className="list-item-hint">
                                        {productsAmountText} <I18n id="administration.permissions.advanced.products" />
                                    </span>
                                </span>
                            </>
                        </button>
                    ) : (
                        <span>
                            <span>{label}</span> <br />
                            {hasProductTypes && (
                                <span className="list-item-hint">
                                    <span>
                                        <b>Tipos de productos</b>
                                    </span>
                                    {productTypes.map((productType) => (
                                        <>
                                            {values.includes(`ALL_${productType}`) ? (
                                                <>
                                                    <span> | </span>
                                                    <I18n id={`productType.${productType}`} />
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </>
                                    ))}
                                </span>
                            )}
                            <br />
                            {hasProducts && (
                                <span className="list-item-hint">
                                    <span>
                                        <b>Productos</b>
                                    </span>
                                    {filteredProducts.map((product) => (
                                        <>
                                            {values.includes(product.value) ? (
                                                <>
                                                    <span> | </span>
                                                    <span>{product.label}</span>
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </>
                                    ))}
                                </span>
                            )}
                            <br />
                        </span>
                    );
                }}
            </Field>
        );
    }
}

const mapStateToProps = (state) => ({
    products: permissionsSelectors.getMappedProducts(state),
});

export default compose(
    connect(mapStateToProps),
    withContext(PermissionsPanelContext, "permissionsPanelContext"),
    withContext(AccordionContext, "accordionContext"),
)(PermissionField);
